'use client'

import * as React from 'react'

const query = (q: string) => (typeof window === 'undefined' ? null : window.matchMedia(q))

/** A media query read as an external store (no state mirrored in an effect). Server render assumes false. */
export function useMedia(q: string) {
  return React.useSyncExternalStore(
    (cb) => {
      const m = query(q)
      m?.addEventListener('change', cb)
      return () => m?.removeEventListener('change', cb)
    },
    () => !!query(q)?.matches,
    () => false,
  )
}

export const useReducedMotion = () => useMedia('(prefers-reduced-motion: reduce)')

const noSubscribe = () => () => {}
/** False on the server and during hydration, true afterwards: for parts that exist only in the browser. */
export const useIsClient = () => React.useSyncExternalStore(noSubscribe, () => true, () => false)

/** Touch uses native scrolling; mouse/pen dragging gets the same direct control without opening a dragged link. */
export function useDragScroll<T extends HTMLElement>(ref: React.RefObject<T | null>, onTakeover?: () => void) {
  const drag = React.useRef<{ x: number; y: number; left: number; id: number } | null>(null)
  const moved = React.useRef(false)
  return {
    onPointerDown: (e: React.PointerEvent<T>) => {
      if (!e.isPrimary || e.button !== 0) return
      onTakeover?.()
      moved.current = false
      if (e.pointerType === 'touch' || !ref.current) return
      drag.current = { x: e.clientX, y: e.clientY, left: ref.current.scrollLeft, id: e.pointerId }
    },
    onPointerMove: (e: React.PointerEvent<T>) => {
      const from = drag.current, el = ref.current
      if (!from || !el || from.id !== e.pointerId) return
      const dx = e.clientX - from.x, dy = e.clientY - from.y
      if (!moved.current && (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy))) return
      moved.current = true
      el.setPointerCapture(e.pointerId)
      e.preventDefault()
      el.scrollLeft = from.left - dx
    },
    onPointerUp: () => { drag.current = null },
    onPointerCancel: () => { drag.current = null },
    onDragStartCapture: (e: React.DragEvent) => e.preventDefault(),
    onClickCapture: (e: React.MouseEvent) => { if (moved.current) { e.preventDefault(); e.stopPropagation(); moved.current = false } },
  }
}
